import React, { useState } from 'react';
import { SubmitHandler, useForm } from 'react-hook-form';
import classNames from 'classnames';
import { JSX as CuiModalJSX } from '@congstar/cui-modal/dist/loader';
import TextField from '../../components/TextField/TextField';
import InputFieldAddon from '../../components/InputField/InputFieldAddon';
import './PasswordResetRequest.scss';
import { requestEmailCode } from '../../api/requestEmailCode';
import { useNavigate } from 'react-router-dom';
import { AxiosError } from 'axios';
import {
  CustomerNumber,
  PATTERN as CustomerNumberPattern,
} from '../../models/CustomerNumber';
import { LastName, PATTERN as LastNamePattern } from '../../models/LastName';
import { ErrorType } from './PasswordResetRequestError/PasswordResetRequestError';
import Button from '../../components/Button/Button';
import { createTrackingObject } from '../../trackingService';
import useSourceContext from '../../hooks/useSourceContext';

interface PasswordResetRequestForm {
  customerNumber: CustomerNumber;
  lastName: LastName;
}

const MAXIMUM_ALLOWED_ATTEMPTS = 3;

export interface PasswordResetRequestProps {
  setTrackingObject: (trackingObject: Record<string, string>) => void;
}

const PasswordResetRequest: React.FC<PasswordResetRequestProps> = ({
  setTrackingObject,
}) => {
  const navigate = useNavigate();
  const form = useForm<PasswordResetRequestForm>();
  const { isSubmitting } = form.formState;
  const [showErrorMessage, setShowErrorMessage] = useState<boolean>(false);
  const [errorCount, setErrorCount] = useState<number>(0);
  const sourceContext = useSourceContext();

  const openInfoModal = (event: React.SyntheticEvent, modalId: string) => {
    event.preventDefault();
    const modal = document.querySelector(modalId) as CuiModalJSX.CuiModal;
    if (modal) {
      modal.isOpen = !modal.isOpen;
    }
  };

  const navigateToErrorPage = (errorType: ErrorType) => {
    return navigate('error', {
      state: {
        errorType,
      },
    });
  };

  const onSubmit: SubmitHandler<PasswordResetRequestForm> = ({
    customerNumber,
    lastName,
  }) => {
    return requestEmailCode(customerNumber, lastName, sourceContext)
      .then(function () {
        setTrackingObject(
          createTrackingObject(
            'login.passwort-vergessen-email.email-anfordern.erfolgreich',
            undefined,
          ),
        );
        return navigate('success');
      })
      .catch(function (error: AxiosError) {
        setTrackingObject(
          createTrackingObject(
            'login.passwort-vergessen-email.email-anfordern.fehler',
            undefined,
          ),
        );
        if (error.response && error.response.status === 412) {
          return navigate('unavailable');
        }
        if (error.response && error.response.status >= 500) {
          return navigateToErrorPage('TECHNICAL_ERROR');
        }
        if (errorCount >= MAXIMUM_ALLOWED_ATTEMPTS) {
          return navigateToErrorPage('FAILED_VERIFICATION_ERROR');
        }
        setShowErrorMessage(true);
        setErrorCount((prevState) => prevState + 1);
      });
  };

  return (
    <div className={'password-reset-request'}>
      {isSubmitting && (
        <div className="loading-overlay loading-overlay--bright">
          <div className="loading-icon loading-icon--centered" />
        </div>
      )}
      <h3 className={'password-reset-request__title'}>
        Du hast dein Passwort vergessen?
      </h3>
      <div className={'password-reset-request__description'}>
        <p>
          Kein Problem! Wir senden dir einen Link per E-Mail, über den du ein
          neues Passwort vergeben kannst.
        </p>
        {showErrorMessage && (
          <p className="password-reset-request__error-notice notice notice--error">
            Die eingegebene Kundennummer und der Nachname passen nicht zusammen.
            Bitte überprüfe beide Eingaben.
          </p>
        )}
      </div>
      <form
        onSubmit={form.handleSubmit(onSubmit)}
        autoComplete="off"
        noValidate
      >
        <TextField
          {...form.register('customerNumber', {
            required: true,
            pattern: CustomerNumberPattern,
          })}
          name="customerNumber"
          label="Kundennummer"
          id="customerNumber"
          className={classNames(
            form.formState.errors.customerNumber && 'input--error',
          )}
          addon={
            <InputFieldAddon
              iconClass="icon--form-help"
              data-testid="customerNumber-help"
              onClick={(event) =>
                openInfoModal(event, 'cui-modal#customerNumberInfoModal')
              }
            />
          }
        />
        <cui-modal
          id="customerNumberInfoModal"
          headline="Wo finde ich meine Kundennummer?"
        >
          <content-loader-container content-id="OGZyTlZZRDhNM1R3czRCQlBKblB6QT09" />
        </cui-modal>
        <TextField
          {...form.register('lastName', {
            required: true,
            pattern: LastNamePattern,
          })}
          name="lastName"
          label="Nachname"
          id="lastName"
          className={classNames(
            form.formState.errors.lastName && 'input--error',
          )}
          addon={
            <InputFieldAddon
              iconClass="icon--form-help"
              data-testid="lastName-help"
              onClick={(event) =>
                openInfoModal(event, 'cui-modal#lastNameInfoModal')
              }
            />
          }
        />
        <cui-modal
          id="lastNameInfoModal"
          headline="Welchen Nachnamen soll ich angeben?"
        >
          <content-loader-container content-id="ak9sbUlZeXdXbnpZb2J0bzhhMlcrQT09" />
        </cui-modal>
        <div className="row">
          <div className="password-reset-request__submit col-12 col-md-5">
            <Button type={'submit'} width={'fullWidth'}>
              Link per E-Mail anfordern
            </Button>
          </div>
        </div>
      </form>
    </div>
  );
};

export default PasswordResetRequest;
